import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, Input } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { PostDiligenceParams } from 'src/app/core/models/params';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ExpiryDateFormatterDirective } from 'src/app/core/directives/format-expiration.directive';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EMPTY, Observable, catchError, forkJoin, from, of, switchMap, take, tap, timer, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiService, MaritimeRequestResponse, PaidManualRequestPayload, PaymentCheckout, PaymentVerificationInput, SubscriptionBenefit } from 'src/app/core/services/api.service';
import { Package } from '../../../core/models/package';
import { environment } from 'src/environments/environment';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TosComponent } from 'src/app/features/tos/tos.component';
import {packages} from 'src/app/core/models/packages';
import { Credit } from 'src/app/core/models/credit';
import { PackageSelectionComponent } from '../package-selection/package-selection.component';
import { IfieldsService, IfieldsVerification } from 'src/app/core/services/ifields.service';

interface OrderSummary {
  totalReports: number;
  availableCredits: number;
  creditsToUse: number;
  reportsNeedingPayment: number;
  totalPaymentNeeded: number;
  benefitsToUse: number;
  benefitDeduction: number;
  uboTotal: number;
  baseTotal: number;
  priceBeforeDeduction: number;
}

@Component({
  selector: 'app-confirm-report',
  templateUrl: './confirm-report.component.html',
  styleUrls: ['./confirm-report.component.scss', './confirm-report.ifields.scss'],
  standalone: true,
  imports: [
    MatButtonModule, CommonModule, MatDialogModule, MatFormFieldModule, MatSelectModule, MatInputModule, FormsModule, ReactiveFormsModule,
    MatFormFieldModule, MatProgressSpinnerModule, MatIconModule, ExpiryDateFormatterDirective, MatCheckboxModule, PackageSelectionComponent
  ],
  providers: [MatSnackBar]
})
export class ConfirmReportComponent implements OnInit {
  apiService = inject(ApiService);
  snackBar = inject(MatSnackBar);
  ifields = inject(IfieldsService);
  sanitizer = inject(DomSanitizer);
  dialog = inject(MatDialog);
  packages = packages;

  // New inputs for standalone mode
  @Input() isStandaloneMode: boolean = false;
  @Input() data: any[] = [];
  @Input() selectedPackages: Package[] = [];
  @Input() uboSelected: boolean = false;
  @Input() manualPaidRequest = false;
  @Input() manualRequestContext: Record<string, unknown> = {};

  formGroup!: FormGroup;
  userHasPayed = false;
  loading = false;
  error = false;
  paymentErrorMessage = 'We were unable to process your payment. Verify the details and try again.';
  termsAccepted = false;
  hasCredits = false;
  checkingCredits = true;
  private paidRequestIdempotencyKey = crypto.randomUUID();
  private pendingCheckoutId: string | null = null;
  private paidCheckoutId: string | null = null;
  fulfillmentComplete = false;
  confirmationTitle = '';
  confirmationMessage = '';
  ifieldsReady = false;
  ifieldsError = '';
  readonly ifieldFrameUrl = `https://cdn.cardknox.com/ifields/${environment.ifields.version}/ifield.htm`;
  readonly safeIfieldFrameUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.ifieldFrameUrl);

  credits: Credit[] = [];
  benefits: SubscriptionBenefit[] = [];
  orderSummary: OrderSummary = {
    totalReports: 0,
    availableCredits: 0,
    creditsToUse: 0,
    reportsNeedingPayment: 0,
    totalPaymentNeeded: 0,
    benefitsToUse: 0,
    benefitDeduction: 0,
    uboTotal: 0,
    baseTotal: 0,
    priceBeforeDeduction: 0
  };

  constructor(
    private router: Router
  ) { }

  onSubmit() {
    if (this.formGroup.getRawValue().xAmount > 0) {
      this.onPaySubmit();
    } else {
      this.submitDiligence();
    }
  }

  onPaySubmit() {
    if (!this.formGroup.valid || !this.ifieldsReady) {
      Object.keys(this.formGroup.controls).forEach(key => {
        const control = this.formGroup.get(key);
        control?.markAsTouched();
      });
      if (!this.ifieldsReady) this.ifieldsError = 'Secure payment fields are still loading. Please try again.';
      return;
    }
    this.loading = true;
    this.error = false;
    this.userHasPayed = false;
    this.paymentErrorMessage = 'We were unable to process your payment. Verify the details and try again.';
    this.ifieldsError = '';
    const billing = this.billingDetails();
    this.apiService.createPaymentCheckout({
      packages: this.selectedPackages.map(pkg => pkg.code),
      companies: this.data.map(company => company.target),
      uboSelected: this.uboSelected
    }).pipe(
      tap(checkout => {
        this.pendingCheckoutId = checkout.id;
        if (typeof checkout.amountCents === 'number') {
          const totalPaymentNeeded = checkout.amountCents / 100;
          this.orderSummary = { ...this.orderSummary, totalPaymentNeeded };
          this.formGroup.patchValue({ xAmount: totalPaymentNeeded });
        }
      }),
      switchMap(checkout => from(this.ifields.getTokens()).pipe(switchMap(tokens => this.apiService.attemptPaymentCheckout(checkout.id, {
        ...tokens,
        expiry: this.formGroup.value.xExp.replace(/\D/g, ''),
        billing
      })), switchMap(result => this.finishPaymentResult(result)))),
      catchError(() => {
        this.loading = false;
        this.userHasPayed = false;
        this.error = true;
        return EMPTY;
      })
    ).subscribe();
  }

  private finishPaymentResult(result: PaymentCheckout): Observable<PaymentCheckout> {
    if (result.status === 'requires3ds' && result.transaction && this.pendingCheckoutId) {
      this.ifields.verify3DS(result.transaction);
      return EMPTY;
    }
    this.loading = false;
    if (result.status === 'approved') {
      this.paidCheckoutId = this.pendingCheckoutId;
      this.pendingCheckoutId = null;
      this.error = false;
      this.userHasPayed = true;
      this.submitDiligence(this.paidCheckoutId || undefined);
    } else {
          this.pendingCheckoutId = null;
      this.userHasPayed = false;
      this.paymentErrorMessage = result.reason ? `Payment declined: ${result.reason}` : 'We were unable to process your payment. Verify the details and try again.';
      this.error = true;
    }
    return EMPTY;
  }

  private billingDetails(): Record<string, string> {
    const value = this.formGroup.value;
    return {
      xBillFirstName: value.xBillFirstName,
      xBillLastName: value.xBillLastName,
      xBillStreet: value.xBillStreet,
      xBillCity: value.xBillCity,
      xBillState: value.xBillState,
      xBillZip: value.xBillZip,
      xBillMobile: value.xBillMobile
    };
  }

  private on3dsVerification(verification: IfieldsVerification): void {
    if (!this.pendingCheckoutId) return;
    const checkoutId = this.pendingCheckoutId;
    this.apiService.verifyPaymentCheckout3ds(checkoutId, verification as PaymentVerificationInput).subscribe({
      next: result => {
        this.paidCheckoutId = checkoutId;
        this.pendingCheckoutId = null;
        this.loading = false;
        if (result.status === 'approved') {
          this.error = false;
          this.userHasPayed = true;
          this.submitDiligence(this.paidCheckoutId || undefined);
        } else {
          this.userHasPayed = false;
          this.error = true;
        }
      },
      error: () => { this.pendingCheckoutId = null; this.loading = false; this.userHasPayed = false; this.error = true; }
    });
  }

  private initializeIfields(): void {
    this.ifields.loadScript(environment.ifields.scriptUrl).then(() => {
      this.ifields.initialize(environment.ifields.publicKey, verification => this.on3dsVerification(verification));
      this.ifieldsReady = true;
    }).catch(() => { this.ifieldsError = 'Secure payment fields could not be loaded.'; });
  }

  submitDiligence(paymentCheckoutId?: string) {
    if (this.manualPaidRequest) {
      this.loading = true;
      this.error = false;
      this.submitPaidFulfillment(paymentCheckoutId).subscribe({
        next: response => {
          this.loading = false;
          this.userHasPayed = true;
          this.showConfirmation(
            'Request received',
            response.payment?.benefitCode
              ? 'Your included subscription benefit has been applied. Tevuna will notify you when the request is ready.'
              : 'Your manual intelligence request has been received. Tevuna will notify you when it is ready.'
          );
        },
        error: () => {
          this.loading = false;
          this.userHasPayed = Boolean(paymentCheckoutId);
          this.error = true;
          this.paymentErrorMessage = paymentCheckoutId
            ? 'Payment approved, but the paid request could not be created. Please do not pay again.'
            : 'The included benefit could not be applied. Review the checkout and try again.';
          if (!paymentCheckoutId) this.refreshBenefits();
        }
      });
      return;
    }
    this.loading = true;
    this.error = false;

    const diligenceData = {
      companies: this.data.map(company => ({
        country: company.country,
        type: company.type,
        csid: company.id || company.peopleId,
        name: company.name,
        target: company.target
      })),
      packages: this.selectedPackages.map(pkg => pkg.code),
      ubo: this.uboSelected,
      ...(paymentCheckoutId ? { paymentCheckoutId } : {})
    };

    const reportRequest = paymentCheckoutId
      ? this.submitPaidReportFulfillment(diligenceData, paymentCheckoutId)
      : this.apiService.postDiligence(diligenceData);

    reportRequest.subscribe({
      next: (response: { reports?: Array<{ benefitCode?: string; benefitRemaining?: number }> }) => {
        if (!response?.reports?.length) {
          this.loading = false;
          this.userHasPayed = true;
          this.error = true;
          this.paymentErrorMessage = 'Payment approved, but no report was created. Please do not pay again.';
          return;
        }
        this.loading = false;
        this.userHasPayed = true;
        this.error = false;
        const usedBenefit = response.reports.some(report => ['ucb', 'avi'].includes(report?.benefitCode || ''));
        this.showConfirmation(
          'Report created',
          usedBenefit
            ? 'Your included subscription benefit has been applied. Your report and due diligence have been created.'
            : paymentCheckoutId
              ? 'Payment confirmed. Your report and due diligence have been created.'
              : 'Your report and due diligence have been created.'
        );
      },
      error: () => {
        this.loading = false;
        this.userHasPayed = Boolean(paymentCheckoutId);
        this.error = true;
        this.paymentErrorMessage = paymentCheckoutId
          ? 'Payment approved, but the report could not be created. Please do not pay again.'
          : 'The included benefit could not be applied. Review the checkout and try again.';
        if (!paymentCheckoutId) this.refreshBenefits();
      }
    });
  }

  retryReportCreation(): void {
    if (this.paidCheckoutId) this.submitDiligence(this.paidCheckoutId);
  }

  private submitPaidReportFulfillment(diligenceData: PostDiligenceParams, paymentCheckoutId: string, attempt = 0): Observable<{ reports?: unknown[] }> {
    return this.apiService.postDiligence(diligenceData).pipe(
      catchError((error: HttpErrorResponse) => {
        if (!this.isPaymentCreditFulfillmentPending(error) || attempt >= 20) return throwError(() => error);
        return this.apiService.getPaymentCheckout(paymentCheckoutId).pipe(
          switchMap(checkout => checkout.status === 'approved'
            ? timer(700).pipe(switchMap(() => this.submitPaidReportFulfillment(diligenceData, paymentCheckoutId, attempt + 1)))
            : throwError(() => error))
        );
      })
    );
  }

  private isPaymentCreditFulfillmentPending(error: HttpErrorResponse): boolean {
    if (error.status === 409) return true;
    const message = String(error.error?.error || error.error || '');
    return error.status === 400 && /not enough available credits/i.test(message);
  }

  get canRetryReportCreation(): boolean {
    return !!this.paidCheckoutId && !this.manualPaidRequest;
  }

  goToReports(): void {
    this.router.navigate(['/reports']);
  }

  private showConfirmation(title: string, message: string): void {
    this.fulfillmentComplete = true;
    this.confirmationTitle = title;
    this.confirmationMessage = message;
  }

  private submitPaidFulfillment(paymentCheckoutId?: string, attempt = 0): Observable<MaritimeRequestResponse> {
    const payload = this.getPaidManualPayload(paymentCheckoutId);
    return this.apiService.submitPaidManualRequest(payload, this.paidRequestIdempotencyKey).pipe(
      catchError((error: HttpErrorResponse) => {
        if (!paymentCheckoutId || error.status !== 409 || attempt >= 5) return throwError(() => error);
        return this.apiService.getPaymentCheckout(paymentCheckoutId).pipe(
          switchMap(checkout => checkout.status === 'approved'
            ? timer(700).pipe(switchMap(() => this.submitPaidFulfillment(paymentCheckoutId, attempt + 1)))
            : throwError(() => error))
        );
      })
    );
  }

  private getPaidManualPayload(paymentCheckoutId?: string): PaidManualRequestPayload {
    const entity = this.data[0] || {};
    const imo = String(entity.imoNumber ?? entity.imo ?? '').replace(/^IMO\s*/i, '').trim();
    return {
      kind: 'product_request',
      productCode: this.selectedPackages[0]?.code,
      imo: /^\d{7}$/.test(imo) ? imo : null,
      vesselContext: this.manualRequestContext,
      ...(paymentCheckoutId ? { paymentCheckoutId } : {})
    };
  }

  openTermsAndConditions() {
    this.dialog.open(TosComponent, {
      width: '80%',
      height: '80%'
    });
  }



  

  hasUboAvailable(): boolean {
    return this.selectedPackages.some(pkg => pkg.ubo_add_on !== false);
  }

  getUboSupportedPackages(): Package[] {
    return this.selectedPackages.filter(pkg => pkg.ubo_add_on !== false);
  }

  get uboSupportedPackageNames(): string {
    return this.getUboSupportedPackages().map(p => p.name).join(', ');
  }

  updateTotalAmount() {
    this.calculateOrderSummary();
  }

  calculateOrderSummary() {
    const totalReports = this.data.length * this.selectedPackages.length;
    const availableCredits = this.credits.reduce((sum, credit) => sum + credit.remaining_amount, 0);
    const benefitsRemaining = new Map(this.benefits.map(benefit => [benefit.code, benefit.remaining]));
    let benefitsToUse = 0;
    let benefitDeduction = 0;

    const reportPrices = this.data.flatMap((entity) => this.selectedPackages.map(pkg => {
      const benefitCode = this.benefitCodeFor(pkg, entity);
      const remaining = benefitCode ? benefitsRemaining.get(benefitCode) || 0 : 0;
      const benefitApplied = remaining > 0;
      if (benefitApplied && benefitCode) {
        benefitsRemaining.set(benefitCode, remaining - 1);
        benefitsToUse += 1;
        benefitDeduction += pkg.price ?? 0;
      }
      return {
        basePrice: benefitApplied ? 0 : (pkg.price ?? 0),
        uboPrice: this.uboSelected && pkg.ubo_add_on !== false ? pkg.ubo_add_on : 0,
        benefitCode: benefitApplied ? benefitCode : null
      };
    }));

    // Sort by base price descending (most expensive first)
    reportPrices.sort((a, b) => b.basePrice - a.basePrice);

    // Apply credits to most expensive reports first
    const creditsToUse = Math.min(totalReports, availableCredits);
    const reportsNeedingPayment = reportPrices.slice(creditsToUse);

    // Calculate totals
    const baseTotal = reportsNeedingPayment.reduce((sum, report) => sum + report.basePrice, 0);
    
    // UBO is charged only for packages that support it
    const uboTotal = this.uboSelected ? 
      reportPrices.reduce((sum, report) => sum + report.uboPrice, 0) : 0;

    const totalPaymentNeeded = baseTotal + uboTotal;

    this.orderSummary = {
      totalReports,
      availableCredits,
      creditsToUse,
      reportsNeedingPayment: reportsNeedingPayment.length,
      totalPaymentNeeded,
      priceBeforeDeduction: reportPrices.reduce((sum, report) => sum + report.basePrice, 0) + benefitDeduction,
      benefitsToUse,
      benefitDeduction,
      uboTotal,
      baseTotal
    };

    this.formGroup.patchValue({
      xAmount: totalPaymentNeeded
    });
  }

  getCreditDeduction(): number {
    const basePrice = this.orderSummary.priceBeforeDeduction
    return basePrice - this.orderSummary.benefitDeduction - this.orderSummary.baseTotal
  }

  private benefitCodeFor(pkg: Package, entity: any): 'ucb' | 'avi' | null {
    if (this.manualPaidRequest) {
      if (pkg.code === 'R12') return 'avi';
      if (pkg.code === 'R8') return 'ucb';
      return null;
    }
    const imo = String(entity?.target?.imoNumber ?? entity?.target?.imo ?? entity?.imoNumber ?? '').replace(/^IMO\s*/i, '').trim();
    if (pkg.code === 'R8' && /^\d{7}$/.test(imo)) return 'ucb';
    if (pkg.code === 'R12') return 'avi';
    return null;
  }

  private refreshBenefits(): void {
    this.apiService.getMySubscription().pipe(take(1), catchError(() => of({ subscription: null }))).subscribe(({ subscription }) => {
      this.benefits = subscription?.benefits || [];
      this.calculateOrderSummary();
    });
  }

  ngOnInit(): void {
    this.formGroup = new FormGroup({
      "xExp": new FormControl(null, [Validators.required, Validators.minLength(4), Validators.maxLength(5)]),
      "xAmount": new FormControl({ value: null, disabled: true }, Validators.required),
      "xBillFirstName": new FormControl(null, Validators.required),
      "xBillLastName": new FormControl(null, Validators.required),
      "xBillStreet": new FormControl(null, Validators.required),
      "xBillCity": new FormControl(null, Validators.required),
      "xBillState": new FormControl(null, Validators.required),
      "xBillZip": new FormControl(null, [Validators.required, Validators.minLength(5), Validators.maxLength(5)]),
      "xBillMobile": new FormControl(null, [Validators.required, Validators.minLength(10), Validators.maxLength(11)]),
      "termsAccepted": new FormControl(false, Validators.requiredTrue)
    });

    this.initializeIfields();

    forkJoin({
      credits: this.apiService.listCredits().pipe(take(1)),
      subscription: this.apiService.getMySubscription().pipe(take(1), catchError(() => of({ subscription: null })))
    }).subscribe({
      next: ({ credits, subscription: subscriptionResponse }) => {
        this.credits = Array.isArray(credits) ? credits : [];
        this.benefits = subscriptionResponse.subscription?.benefits || [];
        this.checkingCredits = false;
        this.calculateOrderSummary();
      },
      error: () => {
        this.credits = [];
        this.benefits = [];
        this.checkingCredits = false;
        this.calculateOrderSummary();
      }
    });
  }
}
